import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { URLS } from '@/constants';
import { Identifier } from '@/models';
import { PaginatedResponse } from '@/models/api';
import { Company } from '@/models/company';
import { Money } from '@/models/money';
import {
  InboundShipmentSummary,
  ReplenishmentCatalogProduct,
  ReplenishmentInboundOrder,
  ReplenishmentInboundOrdersFilters,
  ReplenishmentInventoryItem,
  ReplenishmentOrder,
  ReplenishmentRetailer,
  UploadCatalogCSVParams,
} from '@/models/replenishment';
import { Warehouse } from '@/models/warehouse';
import { useUserStore } from '@/piniaStores/user/user.store';
import session, { apiSlugSession, createSession } from '@/services/session';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';

/**
 * Get replenishment inventory items
 * @param {object} [payload] - additional config or params
 * @return a list of ReplenishmentInventoryItem objects
 */
export async function getInventory() {
  const params = {
    limit: 12500,
  };

  const url = `${URLS.REPLENISHMENT.INVENTORY}?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.get<ReplenishmentInventoryItem[]>(url, { params });

  return response.data;
}

/**
 * Get replenishment order items for retailers
 * @param {object} [data] Object with params to pass
 * @return a list of ReplenishmentOrder objects
 */
export async function getRetailerOrders(data: { statuses: string[]; includes: string[] }) {
  const params = {
    limit: 9999,
  };
  const { statuses, includes } = data;
  const serializedParams: string[] = [];

  if (Array.isArray(statuses)) {
    serializedParams.push(statuses.map((s) => `status=${s}`).join('&'));
  }

  if (Array.isArray(includes)) {
    serializedParams.push(includes.map((s) => `includes=${s}`).join('&'));
  }

  let url = `${URLS.REPLENISHMENT.ORDERS}?${URLS.CUSTOM_ERROR}`;

  if (serializedParams) {
    url += `&${serializedParams.join('&')}`;
  }

  const response = await apiSlugSession.get<ReplenishmentOrder[]>(url, { params });

  return response.data;
}

/**
 * Get replenishment order list for internal operations admins
 * @param {string} queryString - Serialized query string
 * @returns paginated order list
 */
export async function getAdminOrdersPaginated(queryString: string) {
  const url = `${URLS.REPLENISHMENT.ORDERS}?${URLS.CUSTOM_ERROR}&${queryString}`;

  const response = await session.get<PaginatedResponse<ReplenishmentOrder[]>>(url);

  return response.data;
}

/**
 * Get a single replenishment order for a retailer
 * @param {object} payload - additional config or params
 * @param {object} payload.orderId - the order id
 * @return an ReplenishmentOrder object
 */
export async function getRetailerOrder(payload: { orderId: number }) {
  const { orderId } = payload || {};

  const response = await apiSlugSession.get<ReplenishmentOrder>(
    `${URLS.REPLENISHMENT.ORDERS}/${orderId}?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
}

/**
 * Get a single replenishment order for an admin
 * @param {object} payload - additional config or params
 * @param {object} payload.orderId - the order id
 * @return an ReplenishmentOrder object
 */
export async function getAdminOrder(payload: { orderId: number }) {
  const { orderId } = payload || {};

  const response = await session.get<ReplenishmentOrder>(
    `${URLS.REPLENISHMENT.ORDERS}/${orderId}?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
}

interface OrderItem {
  productId: number;
  quantity: number;
  unitsPerCase: number;
}

/**
 * Create a replenishment order
 * @param {Company} [payload.destination] - the destination store for delivery
 * @param {OrderItem[]} [payload.lineItems] - the order line items
 * @param {String} [payload.status] - the order status; "Submitted" or "Draft"
 * @return a list of ReplenishmentInventoryItem objects
 */
export async function createOrder(payload: {
  destination: Company;
  lineItems: OrderItem[];
  status: 'Draft' | 'Submitted';
}) {
  const userStore = useUserStore();
  const { destination = { slug: userStore.userActiveCompany?.slug }, lineItems = [], status } = payload;

  const body = {
    destinationCompanySlug: destination.slug,
    lineItems,
    status,
  };

  const response = await apiSlugSession.post<ReplenishmentOrder>(
    `${URLS.REPLENISHMENT.ORDERS}?${URLS.CUSTOM_ERROR}`,
    body,
  );

  return response.data;
}

/**
 * Update a replenishment draft order
 * @param {Company} payload.destination - the destination store for delivery
 * @param {OrderItem[]} payload.lineItems - the order line items
 * @param {string} payload.status - the order status; "Submitted" or "Draft"
 * @param {number} payload.orderId - the order id
 * @return a list of ReplenishmentInventoryItem objects
 */
export async function updateDraftOrder(payload: {
  destination: Company;
  lineItems: OrderItem[];
  status: string;
  orderId: number;
}) {
  const userStore = useUserStore();

  const { destination = { slug: userStore.userActiveCompany?.slug }, lineItems = [], status, orderId } = payload;

  const body = {
    destinationCompanySlug: destination.slug,
    lineItems,
    status,
  };

  const response = await apiSlugSession.put<ReplenishmentOrder>(
    `${URLS.REPLENISHMENT.ORDERS}/${orderId}?${URLS.CUSTOM_ERROR}`,
    body,
  );

  return response.data;
}

/**
 * Cancel a replenishment order
 * @param {object} payload - additional config or params
 * @param {object} payload.orderId - the order id
 */
export async function cancelOrder(payload: { orderId: number }) {
  const { orderId } = payload || {};

  const response = await apiSlugSession.delete<ReplenishmentOrder>(`${URLS.REPLENISHMENT.ORDERS}/${orderId}`);

  return response.data;
}

/**
 * Reprocess a replenishment order
 * @param {object} payload - additional config or params
 * @param {number} payload.orderId - the order id
 */
export async function reprocessOrder(payload: { orderId: number }) {
  const { orderId } = payload;

  const response = await session.post<ReplenishmentOrder>(`${URLS.REPLENISHMENT.ORDERS}/${orderId}/reprocess`);

  return response.data;
}

/**
 * Get a list of replenishment retailers
 */
export async function getRetailers() {
  const response = await session.get<ReplenishmentRetailer[]>(`${URLS.REPLENISHMENT.RETAILERS}`);

  return response.data;
}

/**
 * Get a replenishment retailer by its id
 * @param {object} payload
 * @param {string} payload.retailerId - the id of the replenishment retailer
 */
export async function getRetailer(payload: { retailerId: string }) {
  const { retailerId } = payload || {};

  const response = await session.get<ReplenishmentRetailer>(`${URLS.REPLENISHMENT.RETAILERS}/${retailerId}`);

  return response.data;
}

/**
 * Edit a replenishment retailer
 * @param {object} payload
 * @param {string} payload.retailerId - the id of the replenishment retailer
 * @param {any} [payload.fieldName] - placeholder for fields that can be updated
 */
export async function editRetailer(payload: { retailerId: string }) {
  const { retailerId, ...fields } = payload || {};

  const response = await session.patch<ReplenishmentRetailer>(`${URLS.REPLENISHMENT.RETAILERS}/${retailerId}`, fields);

  return response.data;
}

/**
 * Get a list of warehouses used in the replenishment experience
 */
export async function getWarehouses() {
  const response = await session.get<Warehouse[]>(`${URLS.REPLENISHMENT.WAREHOUSES}`);

  return response.data;
}

/**
 * Get a list of destination companies that belong to all company groups who have replenishment enabled.
 * For internal admins
 */
export async function getRetailerDestinations() {
  const response = await session.get<Identifier[]>(`${URLS.REPLENISHMENT.DESTINATIONS}`);

  return response.data;
}

/**
 * Get a list of inbound orders. endpoint should be completed in ticket LOGX-414
 */
export async function getInboundOrders(payload: ServerListViewChangeData['serialized']) {
  const params = payload.replace('seller_id', 'seller');
  const url = `${URLS.REPLENISHMENT.INBOUND_ORDERS}?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get<PaginatedResponse<ReplenishmentInboundOrder>>(url);

  return response.data;
}

/**
 * endpoint for inbound order filter data
 */
export async function getOrdersInboundFilters() {
  const url = `${URLS.REPLENISHMENT.INBOUND_ORDERS_FILTERS}?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.get<ReplenishmentInboundOrdersFilters>(url);

  return response.data;
}

export interface CreateInboundShipmentArgs {
  shipmentData: {
    inboundShipmentId: InboundShipmentSummary['orderId'];
    lineItems: Array<{
      productId: number;
      quantity: number;
      unitSellPrice: Money;
    }>;
  };
}

export async function createInboundShipment({ shipmentData }: CreateInboundShipmentArgs) {
  const url = `${URLS.REPLENISHMENT.INBOUND_ORDERS}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.post(url, shipmentData);

  return response.data;
}

export interface GetInboundShipmentSummaryArgs {
  uuid: InboundShipmentSummary['orderNumber'];
}

export interface CancelInboundShipmentArgs {
  uuid: InboundShipmentSummary['orderNumber'];
}

/**
 * Get all the details for one Inbound Shipment
 */
export async function getInboundShipmentSummary({ uuid }: GetInboundShipmentSummaryArgs) {
  const url = `${buildPath(URLS.REPLENISHMENT.INBOUND_SHIPMENT_SUMMARY, { uuid })}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<InboundShipmentSummary>(url);

  return response.data;
}

export async function deleteInboundShipment({ uuid }: CancelInboundShipmentArgs) {
  const url = `${URLS.REPLENISHMENT.INBOUND_ORDERS}/${uuid}`;

  const response = await apiSlugSession.delete<ReplenishmentInboundOrder>(url);

  return response.data;
}

export interface InboundShipmentPutBody extends InboundShipmentSummary {
  inboundShipmentId: InboundShipmentSummary['orderNumber'];
}

export interface PutInboundShipmentArgs {
  uuid: InboundShipmentSummary['orderNumber'];
  data: InboundShipmentPutBody;
}

export async function putInboundShipment({ uuid, data }: PutInboundShipmentArgs) {
  const url = `${buildPath(URLS.REPLENISHMENT.INBOUND_SHIPMENT_SUMMARY, { uuid })}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.put<InboundShipmentSummary>(url, data);

  return response.data;
}

export interface GetCatalogProductsArgs {
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    ordering?: string;
    companyName?: string;
  };
}

/**
 * Get a list of replenishment catalog products.
 */
export async function getCatalogProducts({ params }: GetCatalogProductsArgs) {
  const url = URLS.REPLENISHMENT.CATALOG;
  const paramsFormatted = new JSONTransform({ errorFormat: 'jsonapi', ...params }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<PaginatedResponse<ReplenishmentCatalogProduct>>(url, {
    params: paramsFormatted,
  });

  return response.data;
}

/**
 * Sends a `.csv` file with all catalog products via email
 */
export const sendCatalogProductsCSVToEmail = async () => {
  const url = `${URLS.REPLENISHMENT.CATALOG}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get(url, {
    headers: {
      accept: 'text/csv',
    },
  });

  return response.data;
};

export async function getCatalogCSVUploadLink(filename: File['name']) {
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const url = `${URLS.REPLENISHMENT.CATALOG_FILE_UPLOAD}?filename=${filename}`;
  const response = await session.get(url);

  return response.data;
}

export async function uploadCatalogCSV({ file, headers, url }: UploadCatalogCSVParams) {
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const response = await formSession.put(url, file, { headers });

  return response.data;
}

/**
 * Get a list of replenishment catalog companies.
 */
export async function getCatalogCompanies() {
  const url = `${URLS.REPLENISHMENT.CATALOG_COMPANIES}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<Array<ReplenishmentCatalogProduct['companyName']>>(url);

  return response.data;
}

export interface PutCatalogProductParams {
  product: ReplenishmentCatalogProduct;
}

/**
 * Update a single catalog product by replacing the entire product with the provided data.
 */
export async function putCatalogProduct({ product }: PutCatalogProductParams) {
  const url = buildPath(URLS.REPLENISHMENT.CATALOG_PRODUCT, { productId: product.id });

  const response = await apiSlugSession.put<ReplenishmentCatalogProduct>(url, product);

  return response.data;
}
